import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { fredResearchAPI, marketResearchAPI } from '../services/api';

// Types
interface FredYieldCurvePoint {
  tenor: string;
  series_id: string;
  yield: number | null;
  date: string | null;
  source: string | null;
  daily_change_bps: number | null;
  weekly_change_bps: number | null;
  monthly_change_bps: number | null;
}

interface FredYieldCurveData {
  as_of_date: string;
  curve: FredYieldCurvePoint[];
  spreads: {
    '2s10s'?: number;
    '2s30s'?: number;
    '5s30s'?: number;
    '3m10y'?: number;
  };
  tips_real_yields: Array<{ tenor: string; real_yield: number }>;
  breakeven_inflation: Array<{ tenor: string; breakeven: number }>;
}

interface MarketIndex {
  symbol: string;
  name: string;
  price: number | null;
  change: number | null;
  change_pct: number | null;
  date: string | null;
  sparkline: number[];
  source: string | null;
  is_realtime: boolean;
}

interface MarketIndicesData {
  as_of: string;
  market_status: string;
  indices: MarketIndex[];
}

// Helper components
function MiniSparkline({
  data,
  color = '#6366f1',
  width = 80,
  height = 24,
  showArea = true
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  showArea?: boolean;
}) {
  const chartData = data.map((value, i) => ({ value, i }));
  const isUp = data[data.length - 1] >= data[0];
  const lineColor = color === 'auto' ? (isUp ? '#10b981' : '#ef4444') : color;

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id={`gradient-${lineColor}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          {showArea && (
            <Area
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              fill={`url(#gradient-${lineColor})`}
              strokeWidth={1.5}
            />
          )}
          {!showArea && (
            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={1.5} dot={false} />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChangeIndicator({ value, suffix = '%' }: { value: number; suffix?: string }) {
  const isPositive = value >= 0;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(value).toFixed(2)}{suffix}
    </span>
  );
}

// Main component
export default function MarketTickerBar() {
  const [indicesData, setIndicesData] = useState<MarketIndicesData | null>(null);

  // Use React Query for FRED data - shared cache across components
  const { data: fredData } = useQuery({
    queryKey: ['fred-yield-curve'],
    queryFn: async () => {
      const res = await fredResearchAPI.getYieldCurve<FredYieldCurveData>();
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - FRED data doesn't change often
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 min
  });

  // Use React Query for indices data
  const { data: initialIndices } = useQuery({
    queryKey: ['market-indices'],
    queryFn: async () => {
      const res = await marketResearchAPI.getIndices<MarketIndicesData>();
      return res.data;
    },
    staleTime: 30 * 1000, // 30 seconds for market data
    refetchOnWindowFocus: false,
  });

  // Update indicesData from query or WebSocket
  useEffect(() => {
    if (initialIndices && !indicesData) {
      setIndicesData(initialIndices);
    }
  }, [initialIndices, indicesData]);

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname === 'localhost' ? 'localhost:8000' : 'api.finexus.net';
    const wsUrl = `${wsProtocol}//${wsHost}/api/research/market/ws/indices`;

    let ws: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connectWebSocket = () => {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected for real-time indices');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'indices_update' && message.data) {
            // Convert WebSocket data to MarketIndicesData format
            const indices = Object.entries(message.data).map(([symbol, data]: [string, any]) => ({
              symbol,
              name: { '^GSPC': 'S&P 500', '^IXIC': 'Nasdaq', '^DJI': 'DJIA', '^RUT': 'Russell 2000' }[symbol] || symbol,
              price: data.price,
              change: data.change,
              change_pct: data.change_pct,
              date: null,
              sparkline: [],
              source: data.source,
              is_realtime: data.source === 'realtime',
            }));

            setIndicesData(prev => ({
              as_of: message.timestamp || new Date().toISOString(),
              market_status: message.market_status || prev?.market_status || 'unknown',
              indices: indices.length > 0 ? indices : (prev?.indices || []),
            }));
          } else if (message.type === 'ping') {
            // Respond to ping with pong
            ws?.send(JSON.stringify({ type: 'pong' }));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected, reconnecting in 5s...');
        reconnectTimeout = setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    connectWebSocket();

    return () => {
      if (ws) {
        ws.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, []);

  // Get 10Y yield for display
  const yield10Y = fredData?.curve?.find(d => d.tenor === '10Y');

  // Get spreads from FRED data
  const spread2s10s = fredData?.spreads?.['2s10s'];
  const isSpreadPositive = spread2s10s != null && spread2s10s > 0;

  // Market status indicator
  const marketStatus = indicesData?.market_status;
  const isRealtime = indicesData?.indices?.some(idx => idx.is_realtime);

  return (
    <div className="bg-gray-900 text-white">
      <div className="px-4 sm:px-6">
        <div className="flex items-center divide-x divide-gray-700 overflow-x-auto">
          {/* Market Status Indicator */}
          <div className="flex items-center gap-2 px-4 py-2 min-w-fit">
            <div className={`w-2 h-2 rounded-full ${
              marketStatus === 'open' ? 'bg-green-500 animate-pulse' :
              marketStatus === 'pre-market' ? 'bg-yellow-500' :
              marketStatus === 'after-hours' ? 'bg-orange-500' :
              'bg-gray-500'
            }`} />
            <span className="text-[10px] text-gray-400 uppercase">
              {marketStatus === 'open' ? (isRealtime ? 'Live' : 'Open') :
               marketStatus === 'pre-market' ? 'Pre-Mkt' :
               marketStatus === 'after-hours' ? 'After-Hrs' :
               'Closed'}
            </span>
          </div>
          {indicesData?.indices?.map((idx, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2 min-w-fit">
              <div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wide">{idx.name}</div>
                <div className="text-sm font-medium">
                  {idx.price != null ? idx.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--'}
                </div>
              </div>
              {idx.sparkline && idx.sparkline.length > 0 && (
                <MiniSparkline data={idx.sparkline} color="auto" width={50} height={20} />
              )}
              {idx.change_pct != null && <ChangeIndicator value={idx.change_pct} />}
            </div>
          ))}
          <div className="flex items-center gap-3 px-4 py-2 min-w-fit">
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">10Y Yield</div>
              <div className="text-sm font-medium">
                {yield10Y?.yield != null ? `${yield10Y.yield.toFixed(2)}%` : '--'}
              </div>
            </div>
            {yield10Y?.daily_change_bps != null && (
              <ChangeIndicator value={yield10Y.daily_change_bps} suffix=" bp" />
            )}
          </div>
          <div className="flex items-center gap-3 px-4 py-2 min-w-fit">
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">2s10s</div>
              <div className={`text-sm font-medium ${isSpreadPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                {spread2s10s != null ? `${isSpreadPositive ? '+' : ''}${spread2s10s} bp` : '--'}
              </div>
            </div>
            {spread2s10s != null && (
              <span className="text-[10px] text-gray-500">
                {isSpreadPositive ? 'Curve normal' : 'Curve inverted'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
